import React, { useEffect, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, label, btn, btnGhost } from "../drawings/_drawingsCommon";

const CAT_COLORS = {
  process: "#3aa0ff",
  utility: "#84cc16",
  safety: "#ef4444",
  instrumentation: "#f59e0b",
};

export default function TemplateLibraryPanel({ projectId }) {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("all");
  const [instances, setInstances] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [tplRes, instRes] = await Promise.all([
        api.get(`/projects/${projectId}/ai/templates`),
        api.get(`/projects/${projectId}/ai/templates/instances`),
      ]);
      setTemplates(tplRes.data?.templates || []);
      setCategories(tplRes.data?.categories || []);
      setInstances(instRes.data || []);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    }
  };

  useEffect(() => { load(); }, [projectId]);

  const instantiate = async (templateId) => {
    setBusy(true); setErr("");
    try {
      await api.post(`/projects/${projectId}/ai/templates/instantiate`, { template_id: templateId });
      load();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    } finally { setBusy(false); }
  };

  const filtered = filter === "all" ? templates : templates.filter((t) => t.category === filter);

  return (
    <div data-testid="tpl-panel" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ ...card, flexDirection: "row", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ ...label, marginBottom: 2 }}>Template Library — {templates.length} pre-built assemblies</div>
          <div style={{ fontSize: 11, color: "var(--pf-muted)" }}>
            Reusable engineering assemblies (pump stations, control valve sets, PSV installs, vessel instruments, flare systems, ESD valves, etc.) — drop into your design with one click.
          </div>
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <button data-testid="tpl-filter-all" style={filter === "all" ? btn : btnGhost} onClick={() => setFilter("all")}>All</button>
          {categories.map((c) => (
            <button key={c} data-testid={`tpl-filter-${c}`}
              style={filter === c ? btn : btnGhost} onClick={() => setFilter(c)}>{c}</button>
          ))}
        </div>
      </div>

      {err && <div style={{ padding: 8, color: "#FF8888", background: "rgba(239,68,68,.1)", borderRadius: 4, fontSize: 12 }}>{err}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
        {filtered.map((t) => (
          <div key={t.id} style={card} data-testid={`tpl-card-${t.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <strong style={{ fontSize: 13, color: "#F26B1F" }}>{t.name}</strong>
              <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 3,
                              background: (CAT_COLORS[t.category] || "#888") + "30",
                              color: CAT_COLORS[t.category] || "#888", fontWeight: 700,
                              textTransform: "uppercase" }}>{t.category}</span>
            </div>
            <div style={{ fontSize: 11, color: "var(--pf-muted)", lineHeight: 1.4, marginBottom: 8 }}>
              {t.description}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
              {(t.tags || []).slice(0, 6).map((tag, i) => (
                <span key={i} style={{ fontSize: 9, padding: "2px 6px", background: "var(--pf-surface-2)",
                                        color: "var(--pf-fg)", borderRadius: 3, fontFamily: "monospace" }}>{tag}</span>
              ))}
              {(t.tags || []).length > 6 && (
                <span style={{ fontSize: 9, padding: "2px 6px", color: "var(--pf-muted)" }}>+{t.tags.length - 6} more</span>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 10, color: "var(--pf-muted)" }}>{t.components} components</span>
              <button style={btn} data-testid={`tpl-instantiate-${t.id}`}
                onClick={() => instantiate(t.id)} disabled={busy}>
                Instantiate →
              </button>
            </div>
          </div>
        ))}
      </div>

      {instances.length > 0 && (
        <div style={card}>
          <div style={{ ...label, marginBottom: 6 }}>Instantiated in this Project · {instances.length}</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ background: "var(--pf-surface-2)" }}>
                  {["Instance", "Template", "Components", "When", "By"].map((h) =>
                    <th key={h} style={{ padding: 6, textAlign: "left", color: "var(--pf-muted)", textTransform: "uppercase", fontSize: 9 }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {instances.map((i) => (
                  <tr key={i.instance_id} style={{ borderBottom: "1px solid var(--pf-border)" }}>
                    <td style={{ padding: 6, fontFamily: "monospace", color: "#F26B1F" }}>{i.instance_id}</td>
                    <td style={{ padding: 6 }}>{i.template_name}</td>
                    <td style={{ padding: 6 }}>{i.components}</td>
                    <td style={{ padding: 6, fontSize: 10 }}>{i.instantiated_at?.slice(0, 16).replace("T", " ")}</td>
                    <td style={{ padding: 6, fontSize: 10, color: "var(--pf-muted)" }}>{i.user_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
